import { useEffect, useMemo, useState } from 'react';
import { deleteIvrConfig, fetchIvrConfigs, upsertIvrConfig } from '../api';
import type { IvrConfig, IvrOption } from '../types';

const EMPTY_OPTION: IvrOption = {
  digit: '',
  label: '',
  topic: '',
  tier: 2,
  tip: '',
  opening_line: '',
  auto_wa_template: '',
};

type EditState = {
  campaign: string;
  display_name: string;
  greeting: string;
  greeting_audio_url: string;
  thank_you_audio_url: string;
  options: IvrOption[];
  no_input_tier: number;
  no_input_topic: string;
  no_input_tip: string;
  no_input_opening: string;
  voice: 'woman' | 'man';
  active: boolean;
  business_hours_start: string;
  business_hours_end: string;
  business_days: string;
  timezone: string;
  after_hours_greeting: string;
  after_hours_greeting_audio_url: string;
  after_hours_options: IvrOption[];
  support_phone: string;
};

const BLANK_EDIT: EditState = {
  campaign: '',
  display_name: '',
  greeting: '',
  greeting_audio_url: '',
  thank_you_audio_url: '',
  options: [{ ...EMPTY_OPTION, digit: '1', tier: 1 }],
  no_input_tier: 3,
  no_input_topic: 'no_response',
  no_input_tip: '',
  no_input_opening: '',
  voice: 'woman',
  active: true,
  business_hours_start: '09:00',
  business_hours_end: '18:00',
  business_days: '1,2,3,4,5,6',
  timezone: 'Asia/Kolkata',
  after_hours_greeting: '',
  after_hours_greeting_audio_url: '',
  after_hours_options: [],
  support_phone: '',
};

function parseOptions(json: string): IvrOption[] {
  try {
    return JSON.parse(json) as IvrOption[];
  } catch {
    return [];
  }
}

function configToEdit(c: IvrConfig): EditState {
  return {
    campaign: c.campaign,
    display_name: c.display_name ?? '',
    greeting: c.greeting,
    greeting_audio_url: c.greeting_audio_url ?? '',
    thank_you_audio_url: c.thank_you_audio_url ?? '',
    options: parseOptions(c.options_json),
    no_input_tier: c.no_input_tier,
    no_input_topic: c.no_input_topic,
    no_input_tip: c.no_input_tip ?? '',
    no_input_opening: c.no_input_opening ?? '',
    voice: c.voice,
    active: !!c.active,
    business_hours_start: c.business_hours_start ?? '09:00',
    business_hours_end: c.business_hours_end ?? '18:00',
    business_days: c.business_days ?? '1,2,3,4,5,6',
    timezone: c.timezone ?? 'Asia/Kolkata',
    after_hours_greeting: c.after_hours_greeting ?? '',
    after_hours_greeting_audio_url: c.after_hours_greeting_audio_url ?? '',
    after_hours_options: c.after_hours_options_json ? parseOptions(c.after_hours_options_json) : [],
    support_phone: c.support_phone ?? '',
  };
}

export default function IvrConfigsPanel() {
  const [configs, setConfigs] = useState<IvrConfig[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const d = await fetchIvrConfigs();
      setConfigs(d.configs);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'load failed');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const validationError = useMemo(() => {
    if (!edit) return null;
    if (!edit.campaign.trim()) return 'Campaign slug required';
    if (!/^[a-z0-9_-]+$/i.test(edit.campaign.trim())) {
      return 'Campaign must be alphanumeric / underscore / hyphen only';
    }
    if (!edit.greeting.trim()) return 'Greeting required';
    if (edit.options.length === 0) return 'At least one option required';
    const digits = new Set<string>();
    for (const o of edit.options) {
      if (!/^[0-9]$/.test(o.digit)) return `Digit "${o.digit}" invalid`;
      if (digits.has(o.digit)) return `Duplicate digit ${o.digit}`;
      digits.add(o.digit);
      if (!o.label.trim() || !o.topic.trim() || !o.opening_line.trim()) {
        return `Digit ${o.digit} missing label / topic / opening line`;
      }
    }
    return null;
  }, [edit]);

  async function save() {
    if (!edit || validationError) return;
    setBusy(true);
    try {
      await upsertIvrConfig({
        campaign: edit.campaign.trim(),
        display_name: edit.display_name.trim() || null,
        greeting: edit.greeting.trim(),
        greeting_audio_url: edit.greeting_audio_url.trim() || null,
        thank_you_audio_url: edit.thank_you_audio_url.trim() || null,
        options: edit.options,
        no_input_tier: edit.no_input_tier,
        no_input_topic: edit.no_input_topic,
        no_input_tip: edit.no_input_tip.trim() || null,
        no_input_opening: edit.no_input_opening.trim() || null,
        voice: edit.voice,
        active: edit.active,
        business_hours_start: edit.business_hours_start.trim() || '09:00',
        business_hours_end: edit.business_hours_end.trim() || '18:00',
        business_days: edit.business_days.trim() || '1,2,3,4,5,6',
        timezone: edit.timezone.trim() || 'Asia/Kolkata',
        after_hours_greeting: edit.after_hours_greeting.trim() || null,
        after_hours_greeting_audio_url: edit.after_hours_greeting_audio_url.trim() || null,
        after_hours_options: edit.after_hours_options,
        support_phone: edit.support_phone.trim() || null,
      });
      await load();
      setEdit(null);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'save failed');
    } finally {
      setBusy(false);
    }
  }

  async function remove(campaign: string) {
    if (campaign === 'default') {
      alert('The default config cannot be deleted. Deactivate it instead.');
      return;
    }
    if (!confirm(`Delete IVR config "${campaign}"? Callers on this campaign will fall back to default.`)) return;
    setBusy(true);
    try {
      await deleteIvrConfig(campaign);
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'delete failed');
    } finally {
      setBusy(false);
    }
  }

  // ── editor state helpers ──
  function updateOption(i: number, patch: Partial<IvrOption>) {
    if (!edit) return;
    const next = [...edit.options];
    next[i] = { ...next[i], ...patch };
    setEdit({ ...edit, options: next });
  }
  function addOption() {
    if (!edit) return;
    const used = new Set(edit.options.map((o) => o.digit));
    const nextDigit = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].find((d) => !used.has(d)) ?? '';
    setEdit({ ...edit, options: [...edit.options, { ...EMPTY_OPTION, digit: nextDigit }] });
  }
  function removeOption(i: number) {
    if (!edit) return;
    setEdit({ ...edit, options: edit.options.filter((_, idx) => idx !== i) });
  }

  if (edit) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            {configs.find((c) => c.campaign === edit.campaign) ? 'Edit' : 'New'} IVR config
          </h3>
          <button onClick={() => setEdit(null)} className="text-xs text-slate-500">
            Cancel
          </button>
        </div>

        {validationError && (
          <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800">⚠ {validationError}</p>
        )}

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[10px] font-medium uppercase tracking-wider text-slate-500">
              Campaign slug
            </label>
            <input
              value={edit.campaign}
              onChange={(e) => setEdit({ ...edit, campaign: e.target.value })}
              disabled={!!configs.find((c) => c.campaign === edit.campaign)}
              placeholder="e.g. SKIDS_Meta"
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs disabled:bg-slate-100"
            />
          </div>
          <div>
            <label className="block text-[10px] font-medium uppercase tracking-wider text-slate-500">
              Display name
            </label>
            <input
              value={edit.display_name}
              onChange={(e) => setEdit({ ...edit, display_name: e.target.value })}
              placeholder="SKIDS — Meta ads menu"
              className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-xs"
            />
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-medium uppercase tracking-wider text-slate-500">
            Greeting (spoken to caller)
          </label>
          <textarea
            rows={4}
            value={edit.greeting}
            onChange={(e) => setEdit({ ...edit, greeting: e.target.value })}
            placeholder="Namaste, thank you for calling …"
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-xs"
          />
          <p className="mt-0.5 text-[10px] text-slate-400">
            Spell tricky abbreviations phonetically (I V F not IVF). Keep under ~10 seconds of speech.
          </p>
        </div>

        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3">
          <div className="mb-1.5 flex items-center gap-1.5">
            <span className="text-[11px] font-semibold text-slate-700">🎙️ Pre-recorded audio (optional)</span>
            <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700">
              recommended for Odia / warm voice
            </span>
          </div>
          <p className="mb-2 text-[10px] text-slate-500">
            Paste public MP3/WAV URLs to replace the robotic TTS voice. Host anywhere (Cloudflare R2, S3,
            Cloudinary, your own voice.santaan.in domain). If empty, the worker falls back to the greeting
            text above via Vobiz's built-in TTS.
          </p>
          <label className="mt-1 block text-[10px] font-medium uppercase tracking-wider text-slate-500">
            Greeting audio URL
          </label>
          <input
            value={edit.greeting_audio_url}
            onChange={(e) => setEdit({ ...edit, greeting_audio_url: e.target.value })}
            placeholder="https://voice.santaan.in/ivr/greeting-od.mp3"
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1 font-mono text-[11px]"
          />
          <label className="mt-2 block text-[10px] font-medium uppercase tracking-wider text-slate-500">
            Thank-you audio URL (shared after any digit press)
          </label>
          <input
            value={edit.thank_you_audio_url}
            onChange={(e) => setEdit({ ...edit, thank_you_audio_url: e.target.value })}
            placeholder="https://voice.santaan.in/ivr/thankyou-od.mp3"
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1 font-mono text-[11px]"
          />
        </div>

        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-[10px] font-medium uppercase tracking-wider text-slate-500">
              Options ({edit.options.length})
            </label>
            <button
              onClick={addOption}
              disabled={edit.options.length >= 10}
              className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-700 disabled:opacity-50"
            >
              + Add digit
            </button>
          </div>
          <div className="space-y-2">
            {edit.options.map((o, i) => (
              <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                <div className="mb-1 flex items-center gap-2">
                  <select
                    value={o.digit}
                    onChange={(e) => updateOption(i, { digit: e.target.value })}
                    className="rounded border border-slate-300 px-1 py-0.5 font-mono text-xs"
                  >
                    {['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                  <select
                    value={o.tier}
                    onChange={(e) => updateOption(i, { tier: Number(e.target.value) as 1 | 2 | 3 })}
                    className="rounded border border-slate-300 px-1 py-0.5 text-xs"
                  >
                    <option value={1}>🔴 Tier 1 — HOT</option>
                    <option value={2}>🟡 Tier 2 — WARM</option>
                    <option value={3}>⚫ Tier 3 — COLD</option>
                  </select>
                  <input
                    value={o.label}
                    onChange={(e) => updateOption(i, { label: e.target.value })}
                    placeholder="spoken label"
                    className="flex-1 rounded border border-slate-300 px-2 py-0.5 text-xs"
                  />
                  <button
                    onClick={() => removeOption(i)}
                    className="rounded bg-red-100 px-1.5 text-[10px] font-semibold text-red-700"
                  >
                    ✕
                  </button>
                </div>
                <input
                  value={o.topic}
                  onChange={(e) => updateOption(i, { topic: e.target.value })}
                  placeholder="topic slug (e.g. booking)"
                  className="mb-1 w-full rounded border border-slate-300 px-2 py-0.5 font-mono text-[11px]"
                />
                <input
                  value={o.tip}
                  onChange={(e) => updateOption(i, { tip: e.target.value })}
                  placeholder="internal tip for telecaller"
                  className="mb-1 w-full rounded border border-slate-300 px-2 py-0.5 text-[11px]"
                />
                <input
                  value={o.opening_line}
                  onChange={(e) => updateOption(i, { opening_line: e.target.value })}
                  placeholder="WhatsApp-ready opening line"
                  className="mb-1 w-full rounded border border-slate-300 px-2 py-0.5 text-[11px]"
                />
                <input
                  value={o.auto_wa_template ?? ''}
                  onChange={(e) => updateOption(i, { auto_wa_template: e.target.value })}
                  placeholder="auto-WhatsApp template name (optional — e.g. info_after_call)"
                  className="w-full rounded border border-emerald-200 bg-emerald-50/40 px-2 py-0.5 text-[11px]"
                />
              </div>
            ))}
          </div>
          <p className="mt-1 text-[10px] text-slate-400">
            🪄 <strong>Auto-WA</strong>: when a caller presses this digit, the worker auto-sends the named BhashSMS template.
            Great for info-mode leads (Press 2) — send the blog link + callback number without telecaller effort.
          </p>
        </div>

        <details className="rounded-lg border border-slate-200 bg-white p-2">
          <summary className="cursor-pointer text-[11px] font-semibold text-slate-600">
            🕘 Business hours + time-aware routing
          </summary>
          <div className="mt-2 space-y-1.5">
            <div className="flex items-center gap-2">
              <label className="text-[10px] text-slate-500">Start</label>
              <input
                value={edit.business_hours_start}
                onChange={(e) => setEdit({ ...edit, business_hours_start: e.target.value })}
                placeholder="09:00"
                className="w-20 rounded border border-slate-300 px-2 py-0.5 font-mono text-xs"
              />
              <label className="text-[10px] text-slate-500">End</label>
              <input
                value={edit.business_hours_end}
                onChange={(e) => setEdit({ ...edit, business_hours_end: e.target.value })}
                placeholder="18:00"
                className="w-20 rounded border border-slate-300 px-2 py-0.5 font-mono text-xs"
              />
              <label className="text-[10px] text-slate-500">TZ</label>
              <input
                value={edit.timezone}
                onChange={(e) => setEdit({ ...edit, timezone: e.target.value })}
                placeholder="Asia/Kolkata"
                className="flex-1 rounded border border-slate-300 px-2 py-0.5 font-mono text-xs"
              />
            </div>
            <div>
              <label className="text-[10px] text-slate-500">Business days (0=Sun … 6=Sat, comma-separated)</label>
              <input
                value={edit.business_days}
                onChange={(e) => setEdit({ ...edit, business_days: e.target.value })}
                placeholder="1,2,3,4,5,6"
                className="mt-0.5 w-full rounded border border-slate-300 px-2 py-0.5 font-mono text-xs"
              />
            </div>
            <div>
              <label className="text-[10px] text-slate-500">After-hours greeting audio URL (MP3/WAV)</label>
              <input
                value={edit.after_hours_greeting_audio_url}
                onChange={(e) => setEdit({ ...edit, after_hours_greeting_audio_url: e.target.value })}
                placeholder="https://santaan-crm-audio.pages.dev/ivr/greeting-afterhours.wav"
                className="mt-0.5 w-full rounded border border-slate-300 px-2 py-0.5 font-mono text-[11px]"
              />
            </div>
            <div>
              <label className="text-[10px] text-slate-500">After-hours fallback text (used if audio missing)</label>
              <textarea
                rows={2}
                value={edit.after_hours_greeting}
                onChange={(e) => setEdit({ ...edit, after_hours_greeting: e.target.value })}
                placeholder="Our office is closed. Press 1 morning, 2 afternoon, 3 evening."
                className="mt-0.5 w-full rounded border border-slate-300 px-2 py-0.5 text-[11px]"
              />
            </div>
            <p className="text-[10px] text-slate-500">
              ℹ After-hours menu options (1 morning / 2 afternoon / 3 evening) are seeded in the DB. Edit via SQL if you need different slots — admin UI for those is coming next.
            </p>
          </div>
        </details>

        <details className="rounded-lg border border-slate-200 bg-white p-2">
          <summary className="cursor-pointer text-[11px] font-semibold text-slate-600">
            No-input fallback
          </summary>
          <div className="mt-2 space-y-1.5">
            <div className="flex items-center gap-2">
              <label className="text-[10px] text-slate-500">Tier</label>
              <select
                value={edit.no_input_tier}
                onChange={(e) => setEdit({ ...edit, no_input_tier: Number(e.target.value) })}
                className="rounded border border-slate-300 px-1 py-0.5 text-xs"
              >
                <option value={1}>1 — HOT</option>
                <option value={2}>2 — WARM</option>
                <option value={3}>3 — COLD</option>
              </select>
              <input
                value={edit.no_input_topic}
                onChange={(e) => setEdit({ ...edit, no_input_topic: e.target.value })}
                placeholder="topic"
                className="flex-1 rounded border border-slate-300 px-2 py-0.5 text-xs"
              />
            </div>
            <input
              value={edit.no_input_tip}
              onChange={(e) => setEdit({ ...edit, no_input_tip: e.target.value })}
              placeholder="tip for telecaller"
              className="w-full rounded border border-slate-300 px-2 py-0.5 text-xs"
            />
            <input
              value={edit.no_input_opening}
              onChange={(e) => setEdit({ ...edit, no_input_opening: e.target.value })}
              placeholder="opening line"
              className="w-full rounded border border-slate-300 px-2 py-0.5 text-xs"
            />
          </div>
        </details>

        <details className="rounded-lg border border-slate-200 bg-white p-2">
          <summary className="cursor-pointer text-[11px] font-semibold text-slate-600">
            Support transfer (Press 4)
          </summary>
          <div className="mt-2 space-y-1.5">
            <p className="text-[10px] text-slate-500">
              When a caller presses 4, the call will be transferred to this phone number. Leave empty to disable this feature.
            </p>
            <input
              value={edit.support_phone}
              onChange={(e) => setEdit({ ...edit, support_phone: e.target.value })}
              placeholder="e.g. +919876543210"
              className="w-full rounded border border-slate-300 px-2 py-0.5 font-mono text-xs"
            />
          </div>
        </details>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-slate-700">
            <input
              type="checkbox"
              checked={edit.active}
              onChange={(e) => setEdit({ ...edit, active: e.target.checked })}
            />
            Active
          </label>
          <label className="flex items-center gap-1.5 text-xs text-slate-700">
            Voice
            <select
              value={edit.voice}
              onChange={(e) => setEdit({ ...edit, voice: e.target.value as 'woman' | 'man' })}
              className="rounded border border-slate-300 px-1 py-0.5 text-xs"
            >
              <option value="woman">woman</option>
              <option value="man">man</option>
            </select>
          </label>
        </div>

        <button
          onClick={save}
          disabled={busy || !!validationError}
          className="w-full rounded-lg bg-wechat-accent px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Save IVR config'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {err && <p className="rounded-lg bg-red-50 p-2 text-xs text-red-700">{err}</p>}

      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          IVR configs ({configs.length})
        </h3>
        <button
          onClick={() => setEdit({ ...BLANK_EDIT })}
          className="rounded-full bg-wechat-accent px-3 py-1 text-[11px] font-semibold text-white"
        >
          + New
        </button>
      </div>

      {loading && <p className="text-sm text-slate-500">Loading…</p>}

      <ul className="space-y-2">
        {configs.map((c) => {
          const opts = parseOptions(c.options_json);
          return (
            <li
              key={c.campaign}
              className={`rounded-xl border p-3 ${
                c.active ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200 bg-slate-50'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold">{c.display_name ?? c.campaign}</span>
                    {c.campaign === 'default' && (
                      <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[9px] font-bold text-blue-700">
                        DEFAULT
                      </span>
                    )}
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                        c.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'
                      }`}
                    >
                      {c.active ? 'active' : 'paused'}
                    </span>
                  </div>
                  <div className="mt-0.5 font-mono text-[11px] text-slate-500">{c.campaign}</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {opts.map((o) => (
                      <span
                        key={o.digit}
                        className="rounded-full bg-white px-2 py-0.5 text-[10px] text-slate-700"
                      >
                        {o.digit} → {o.label}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => setEdit(configToEdit(c))}
                    className="rounded-full bg-slate-200 px-2.5 py-1 text-[10px] font-semibold text-slate-700"
                  >
                    Edit
                  </button>
                  {c.campaign !== 'default' && (
                    <button
                      disabled={busy}
                      onClick={() => remove(c.campaign)}
                      className="rounded-full bg-red-100 px-2.5 py-1 text-[10px] font-semibold text-red-700 disabled:opacity-50"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <p className="rounded-lg bg-slate-50 p-2 text-[11px] text-slate-600">
        💡 <strong>How it works:</strong> when a caller dials a Vobiz DID, worker looks up the{' '}
        <code className="rounded bg-white px-1">ad_campaign</code> query param → if a matching config
        exists here, it's used; otherwise falls back to <code className="rounded bg-white px-1">default</code>.
        Add a new config when you launch a new business line (e.g. SKIDS) — no code deploy needed.
      </p>
    </div>
  );
}
